import { MutationStatus } from '@tanstack/react-query';
import { PostQuestionnairePayload } from 'api/typegen/client/questionnaire/post';
import { QuestionnaireFormQuestion, QuestionnaireFormValues } from 'types/ui';
import useQuestionnaireMutation from 'ui/hooks/useQuestionnaireMutation';
import useQuestionnaireQuery from 'ui/hooks/useQuestionnaireQuery';

interface UseQuestionnaire {
  status: MutationStatus;
  isQuestionsLoading: boolean;
  questions: QuestionnaireFormQuestion[];
  questionnaireFormSubmitted: (
    values: QuestionnaireFormValues,
    id?: number,
  ) => Promise<void>;
  questionnaireId?: number;
}

const useQuestionnaire = (): UseQuestionnaire => {
  const questionnaireQuery = useQuestionnaireQuery();
  const questionnaireMutation = useQuestionnaireMutation();
  const questions = questionnaireQuery.questionnaire?.questions || [];

  const questionnaireFormSubmitted = async (
    values: QuestionnaireFormValues,
    id?: number,
  ) => {
    if (!id) return;

    await questionnaireMutation.postQuestionnaire(
      questionnaireFormToPostPayload(id, values),
    );
  };

  return {
    status: questionnaireMutation.status,
    questions,
    isQuestionsLoading: questionnaireQuery.status === 'loading',
    questionnaireFormSubmitted,
    questionnaireId: questionnaireQuery.questionnaire?.id,
  };
};

export default useQuestionnaire;

const questionnaireFormToPostPayload = (
  questionnaireId: number,
  values: QuestionnaireFormValues,
): PostQuestionnairePayload => ({
  questionnaireId,
  answers: Object.values(values).map((value) => {
    const [questionId, answerId] = value.split('_').map(Number);
    return {
      questionId,
      answerId,
    };
  }),
});
